import { createUseStyles } from "react-jss";
import { ITheme } from "../../../constants/theme";
import {
  BreakPointTablet,
  BreakPointMobile,
  BreakPointDesktop,
} from "../../../constants/dimension";
import { isTV } from "../../../utils/platform";
import { GAME_CARD_RATIO, VIDEO_CARD_RATIO } from "../../../constants/gamePack";
import BgRating from "../../../assets/game/background-rating.webp";
import BgRating2x from "../../../assets/game/background-rating@2x.webp";
import BgRating3x from "../../../assets/game/background-rating@3x.webp";
import BgDiscount from "../../../assets/game/background-discount.webp";
import BgDiscount2x from "../../../assets/game/background-discount@2x.webp";
import BgDiscount3x from "../../../assets/game/background-discount@3x.webp";
import BgSteam from "../../../assets/game/background-steam.webp";
import BgSteam2x from "../../../assets/game/background-steam@2x.webp";
import BgSteam3x from "../../../assets/game/background-steam@3x.webp";

const bgRatingImageSet = `-webkit-image-set(url(${BgRating}) 1x, url(${BgRating2x}) 2x, url(${BgRating3x}) 3x)`;
const bgDiscountImageSet = `-webkit-image-set(url(${BgDiscount}) 1x, url(${BgDiscount2x}) 2x, url(${BgDiscount3x}) 3x)`;
const bgSteamImageSet = `-webkit-image-set(url(${BgSteam}) 1x, url(${BgSteam2x}) 2x, url(${BgSteam3x}) 3x)`;

export const useStyles = createUseStyles((theme: ITheme) => ({
  content: {
    display: "flex",
    overflowX: "scroll",
    scrollPaddingInline: "90px",
    scrollbarWidth: "none",
    padding: `${theme.spacing.space24}px 90px`,
    "-ms-overflow-style": "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    gap: theme.spacing.space16,
    [BreakPointTablet]: {
      padding: theme.spacing.space16,
      scrollPaddingInline: "16px",
    },
    [BreakPointMobile]: {
      padding: theme.spacing.space16,
      scrollPaddingInline: "16px",
    },
    [BreakPointDesktop]: {
      gap: theme.spacing.space32,
      ...(!isTV && {
        "& > div:nth-of-type(2)": {
          marginLeft: -90,
        },
        "& > div:nth-last-of-type(2)": {
          marginRight: -90,
        },
      }),
    },
  },
  title: {
    paddingRight: 90,
    paddingLeft: 90,
    [BreakPointTablet]: {
      paddingLeft: theme.spacing.space16,
      paddingRight: theme.spacing.space16,
      fontSize: `18px !important`,
    },
    [BreakPointMobile]: {
      paddingLeft: theme.spacing.space16,
      paddingRight: theme.spacing.space16,
      fontSize: `18px !important`,
    },
  },
  gameImg: {
    cursor: "pointer",
    borderRadius: theme.radius.medium,
    width: "100%",
    minWidth: "100%",
    objectFit: "cover",
    height: "100%",
    minHeight: "100%",
    "&.loading": {
      backgroundColor: "gray",
    },
  },
  selected: {
    position: "relative",
    borderRadius: theme.radius.medium,
    transform: "scale(1.04) translateZ(0)", // Hardware accelerated transform
    willChange: "transform",
    backfaceVisibility: "hidden",
    "&::after": {
      content: `""`,
      position: "absolute",
      borderRadius: theme.radius.medium + 5,
      inset: -8,
      padding: 3,
      ...(isTV
        ? { border: "2px solid rgba(255, 255, 255, 0.8)" }
        : {
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 20%, rgba(255, 255, 255, 0.2) 55%, rgba(255, 255, 255, 0.2) 100%, rgba(255, 255, 255, 0.5) 80%, rgba(255, 255, 255, 0.9) 100%)",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
          }),
    },
  },
  cardSize: {
    flex: "0 0 auto",
    width: "calc(100vw / 6)",
    [BreakPointTablet]: {
      width: "calc(100vw / 4)",
      "@media (orientation: landscape)": {
        width: "calc(100vw / 5)",
      },
    },
    [BreakPointMobile]: {
      width: "calc(100vw / 3)",
      "@media (orientation: landscape)": {
        width: "calc(100vw / 6)",
      },
    },
    "@media (min-width: 480px) and (max-width: 767px) and (max-height: 409px)":
      {
        width: "calc(100vw / 6)",
      },
  },
  cardSizeVideo: {
    flex: "0 0 auto",
    width: "calc(100vw / 4)",
    [BreakPointTablet]: { width: "calc(100vw / 4)" },
    [BreakPointMobile]: { width: "calc(100vw / 2)" },
    "@media (min-width: 480px) and (max-width: 767px) and (max-height: 409px)":
      {
        width: "calc(100vw / 6)",
      },
  },
  contentImg: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.radius.medium,
    border: "0px solid transparent",
    pointerEvents: "auto",
    position: "relative",
    aspectRatio: 1 / GAME_CARD_RATIO,
    ...(isTV && {
      transform: "translateZ(0)",
      willChange: "transform",
      backfaceVisibility: "hidden",
    }),
    [BreakPointDesktop]: {
      transition: "transform 0.3s ease-in-out",
      "&:hover": {
        transform: "scale(1.04)",
      },
    },
  },
  more: {
    position: "absolute",
    bottom: theme.spacing.space8,
    left: theme.spacing.space8,
    display: "flex",
    width: 122,
    height: 46,
    backgroundImage: bgRatingImageSet,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    [BreakPointTablet]: {
      width: 66,
      height: 25,
    },
    [BreakPointMobile]: {
      width: 61,
      height: 23,
      left: theme.spacing.space4,
    },
  },
  steam: {
    backgroundImage: bgSteamImageSet,
    width: 122,
    height: 46,
    [BreakPointTablet]: {
      height: 25,
    },
    [BreakPointMobile]: {
      height: 23,
    },
  },
  info: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.space4,
    bottom: theme.spacing.space8,
    left: theme.spacing.space8,
    position: "absolute",
    [BreakPointTablet]: {
      left: 4,
      bottom: -2,
    },
    [BreakPointMobile]: {
      left: 3,
      bottom: -4,
    },
  },
  star: {
    color: "#FFC500 !important",
  },
  value: {
    [BreakPointTablet]: {
      fontSize: "11px !important",
    },
    [BreakPointMobile]: {
      fontSize: "10px !important",
    },
  },
  discountSec: {
    left: "auto",
    right: theme.spacing.space8,
    marginLeft: "0px !important",
    backgroundImage: bgDiscountImageSet,
    [BreakPointMobile]: {
      right: theme.spacing.space4,
    },
  },
  valueDiscount: {
    position: "absolute",
    right: theme.spacing.space8,
    bottom: theme.spacing.space8,
    left: "auto",
    [BreakPointTablet]: {
      right: 4,
      bottom: -2,
    },
    [BreakPointMobile]: {
      right: 3,
      bottom: -4,
    },
  },
  videoContainer: {
    borderRadius: theme.radius.medium,
    ...(isTV && {
      transform: "translateZ(0)",
      willChange: "transform",
      backfaceVisibility: "hidden",
    }),
    [BreakPointDesktop]: {
      "&:hover": {
        transform: "scale(1.04)",
      },
    },
  },
  video: {
    border: "none",
    borderRadius: theme.radius.small,
    position: "relative",
    overflow: "hidden",
    width: "100%",
    aspectRatio: 1 / VIDEO_CARD_RATIO,
    [BreakPointDesktop]: {
      borderRadius: theme.radius.medium,
    },
  },
  mediaImage: {
    objectFit: "cover",
    height: "100%",
    width: "100%",
    cursor: "pointer",
  },
  statusAccount: {
    width: `calc(100% - ${theme.spacing.space16 * 2}px)`,
    textAlign: "center",
    [BreakPointDesktop]: {
      width: `calc(100% - ${theme.spacing.space32 * 2}px)`,
    },
  },
}));
